import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from '@/ui/primitives';
import { YENILIKLER_ADRESI } from '@/features/support/destek-sekmesi';
import { ReleaseBody, releaseDate } from './ReleaseList';
import { useReleaseNotes } from './api';

/**
 * Which release this browser has already been told about.
 *
 * localStorage, not the server: this is a "have you read it" flag, and putting
 * it in the database would mean a table, a migration, an endpoint and a
 * permission for something whose worst failure is showing a short list twice.
 * The cost of that failure is one click; the cost of the table is paid forever.
 *
 * Per browser, therefore — a user on a phone and a laptop is told twice. That
 * is the deliberate trade, and it is the right way round: being told twice is a
 * nuisance, never being told is the thing this screen exists to prevent.
 */
const SEEN_KEY = 'grouphub.release-notes.seen';

function readSeen(): string | null {
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    // Safari in private mode throws on access. A browser that cannot remember
    // is not a browser that should be blocked from using the application.
    return null;
  }
}

function writeSeen(version: string): void {
  try {
    window.localStorage.setItem(SEEN_KEY, version);
  } catch {
    /* see readSeen */
  }
}

/**
 * "Yenilikler" — shown once, the first time someone opens the app on a version
 * they have not been shown yet.
 *
 * Mounted in the shell so it follows a deploy rather than a route: the person
 * who needs to know what changed is whoever logs in next, wherever they land.
 *
 * It is deliberately shown on a first-ever visit too. The alternative — record
 * the current version silently and only speak up on the NEXT release — reads as
 * tidier and means the feature is invisible on the day it ships, to everyone,
 * including the people who asked for it. One dialog, once, with a "Kapat" is a
 * fair price for the thing working the first time it is looked at.
 */
export function WhatsNewDialog() {
  const notes = useReleaseNotes();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [seen, setSeen] = useState<string | null>(() => readSeen());

  const latest = notes.data?.[0];

  // A release with nothing user-visible in it is not worth a dialog, but it
  // still counts as seen — otherwise the next ordinary open would show the
  // release BEFORE it, which the reader has already dismissed once.
  useEffect(() => {
    if (latest && latest.entries.length === 0 && seen !== latest.version) {
      writeSeen(latest.version);
      setSeen(latest.version);
    }
  }, [latest, seen]);

  if (!latest || latest.entries.length === 0) return null;
  const open = !dismissed && seen !== latest.version;

  const close = () => {
    writeSeen(latest.version);
    setSeen(latest.version);
    setDismissed(true);
  };

  return (
    <Modal
      open={open}
      title="Yenilikler"
      onClose={close}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              close();
              navigate(YENILIKLER_ADRESI);
            }}
          >
            Tüm sürümler
          </Button>
          <Button onClick={close}>Anladım</Button>
        </div>
      }
    >
      <p className="text-fg-muted mb-4 text-sm">
        {latest.version} · {releaseDate(latest.date)}
      </p>
      <ReleaseBody release={latest} />
    </Modal>
  );
}
